import type { CSSObject } from '@emotion/react'
import { Color, FontWeight, Spacing, Typography } from '@opallabs/gem'
import { modernWithFallback } from '@opallabs/lapidary'
import NoiseTexture from './noise-texture.png'
import SpotIllustration from './spot-illustration.png'
import HiresSpotIllustration from './spot-illustration@2x.png'
import { StyleProps } from './types'

// @NOTE All dimensions are based on the 1440×900 design file.

const blobIllustration: CSSObject = {
  opacity: 0.4,
  position: 'absolute',
  zIndex: 0,

  path: {
    fill: '#003BB1',
  },
}

export const bikeSeat: CSSObject = {
  ...blobIllustration,
  ...modernWithFallback(
    [
      {
        insetBlockStart: '29.7vb',
        insetInlineStart: '18vi',
      },
      {
        insetBlockStart: '29.7vh',
        insetInlineStart: '18vw',
      },
    ],
    'width: 1vi'
  ),
}

export const butterfly: CSSObject = {
  ...blobIllustration,
  insetInlineStart: -247,
  transform: `rotate(${70 / 360}turn)`,
  ...modernWithFallback(
    [{ insetBlockStart: '22.3vi' }, { insetBlockStart: '22.3vh' }],
    'width: 1vi'
  ),
}

export const circle = ({ size }: { size: number }): CSSObject => ({
  stroke: '#68E1FD',
  blockSize: size,
  inlineSize: size,
  fill: 'none',
  opacity: 0.2,
  position: 'absolute',
  strokeWidth: 2,
  zIndex: 0,

  // These are hardcoded offsets that match the 1440×900 design file. Unlike
  // the 'blob illustrations', this positioning doesn't benefit from scaling
  // with the viewport.
  insetBlockStart: -225,
  insetInlineEnd: -175,
})

export const container = ({ theme }: StyleProps): CSSObject => ({
  // Dark-theme background is made up--there's not an official color for it,
  // and the light-theme background color isn't part of the official palette.
  backgroundColor: theme === 'light' ? '#003194' : Color[theme].Surface.A200,
  backgroundImage: `url(${NoiseTexture})`,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  maxInlineSize: Spacing.M * 32.125,
  minInlineSize: Spacing.XL * 7.5,
  overflow: 'hidden',
  paddingBlockEnd: Spacing.M * 2.75,
  paddingBlockStart: Spacing.XL * 1.5,
  paddingInlineEnd: Spacing.XL * 1.6875,
  paddingInlineStart: Spacing.M * 2.75,
  position: 'relative',
  ...modernWithFallback(
    [{ inlineSize: '35.7vi' }, { inlineSize: '35.7vw' }],
    'width: 1vi'
  ),

  // When the viewport is very narrow, or the user has zoomed in to 400%, it's
  // not possible to fit the sidebar in the viewport. This is fine, because
  // it's just a decorative element.
  '@media only screen and (max-width: 850px)': {
    display: 'none',
  },
})

export const logo: CSSObject = {
  display: 'flex',
}

export const blockStart: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
}

export const headline = ({ theme }: StyleProps): CSSObject => ({
  color: Color[theme].Text.InvertedDefault,
  fontSize: Spacing.XS * 13,
  fontWeight: FontWeight.Bold,
  lineHeight: `${Spacing.XS * 14}px`,
  // In Sketch the provided `font-size` and `line-height` make the text block
  // 181 pixels high; in the browser, it only comes to 168 pixels.
  marginBlockEnd: Spacing.XS * (16.5 + 3.25),
  marginBlockStart: Spacing.M * 4.625,
})

export const copyright = ({ theme }: StyleProps): CSSObject => ({
  color: Color[theme].Text.InvertedDefault,
  ...Typography.T300,
  marginBlockStart: Spacing.M * 1.25,
  opacity: '80%',
  zIndex: 1,
})

export const spotIllustration: CSSObject = {
  alignSelf: 'center',
  aspectRatio: '1200 / 1229',
  backgroundImage: `url(${SpotIllustration})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  display: 'flex',
  maxBlockSize: Spacing.M * 24.0625,
  maxInlineSize: Spacing.XL * 7.8125,
  zIndex: 1,

  '@media (min-device-pixel-ratio: 2), (-webkit-min-device-pixel-ratio: 2)': {
    backgroundImage: `url(${HiresSpotIllustration})`,
  },

  ...modernWithFallback(
    [
      {
        // In the 1440×900 design file, the illustration is 384.06 pixels wide.
        // This is `384.06 / 1440 ⋅ 100 ≃ 26.6708333...` percent of the total
        // design width. (The size is specified based on the width, rather than
        // the height, to preserve the aspect ratio.)
        //
        // @note As support for the `aspect-ratio` property improves the
        //   `blockSize` rule can be removed.
        blockSize: '26.6708333vi',
        // In the 1440×900 design file, the illustration is 375 pixels wide. This
        // is `375 / 1440 ⋅ 100 ≃ 26.041666...` percent of the todal design
        // width.
        inlineSize: '26.041666vi',
        // In the design file, the illustration is 58 pixels from the left of the
        // sidebar and 81 pixels from the right.
        //
        // The left side of the container has 44 pixels of padding, so the
        // illustration is an extra `(58 - 44) / 1440 ⋅ 100 ≃ 0.97222...` percent
        // of the total design width from the left of the sidebar. The right side
        // of the illustration doesn't need any adjustment, as it matches the
        // container's padding.
        //
        // Using this percentage as a margin keeps the illustration in a
        // consistent off-center position despite the responsive layout.
        marginInlineStart: '0.972vi',
      },
      {
        // @note As support for the `aspect-ratio` property improves the
        //   `blockSize` rule can be removed.
        blockSize: '26.6708333vw',
        inlineSize: '26.041666vw',
        marginInlineStart: '0.972vw',
      },
    ],
    'width: 1vi'
  ),
}
